import classNames from 'classnames';

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { Proposal } from '../../Proposal';
import { ProposalDataElement } from '../../ProposalElements';

import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { PercentageCell, StatCell } from '@/views/components/cells';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import HistoricalScenariosComparisonItemPerformance from '@/types/historical-scenarios-comparison-item-performance';

export default class HistoricalScenariosMetricsDAO extends ComparisonItemBasedProposalDAO {
  metricsFieldClasses = classNames(ProposalStyleClassNames.ProposalTableContent, ProposalStyleClassNames.ProposalTableIndentField);

  i18nBasePath = 'Proposal.Modules.HistoricalScenariosMetrics';

  public static buildInstance(proposal: Proposal, proposalElement: ProposalDataElement): ProposalDAOInterface {
    return new HistoricalScenariosMetricsDAO(proposal, proposalElement);
  }

  protected getColorKeys(): string[] {
    return ['HISTORICAL_SCENARIOS_METRICS_COLORS'];
  }

  protected build(): void {
    let itemDAO = null;

    if (
      this.sectionIteratorIndex === null ||
      !this.proposal.selectedHistoricalScenarios ||
      this.proposal.calculatedHistoricalScenarios === undefined ||
      this.proposal.calculatedHistoricalScenarios.length === 0
    ) {
      return;
    }

    const scenario = this.proposal.selectedHistoricalScenarios[this.sectionIteratorIndex];
    const calculatedScenario = this.proposal.calculatedHistoricalScenarios?.find(s => s.historicalScenario._id === scenario._id);

    if (!calculatedScenario) {
      return;
    }

    while ((itemDAO = this.nextItem()) !== null) {
      const i = calculatedScenario.items.find(i => i._id === itemDAO.item._id);
      if (!i) {
        return;
      }
      const renderConfig = this.itemToMetricsConfig({
        annualizedReturn: i.metrics.annualizedReturns,
        grossReturn: i.metrics.grossReturns,
        standardDeviation: i.metrics.standardDeviation || 0,
      });

      for (const category of renderConfig) {
        this.addFieldDAO(itemDAO, new ProposalFieldDAO('', ''));
        this.addFieldName(category.title, classNames(ProposalStyleClassNames.ProposalTableGroup, ProposalStyleClassNames.ProposalTableContent));

        for (const field of category.fields) {
          this.addMetricField(itemDAO, field.label, field.value, field.cellType);
        }
      }
    }
  }

  addMetricField(itemDAO: ComparisonItemDAO, fieldName: string, fieldValue: any, cellType: any): void {
    const field = new ProposalFieldDAO(fieldName, fieldValue, this.metricsFieldClasses);
    field.cellType = cellType;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(fieldName, this.metricsFieldClasses);
  }

  itemToMetricsConfig = (historicalScenariosMetrics: HistoricalScenariosComparisonItemPerformance | undefined) => {
    if (!historicalScenariosMetrics) {
      return [];
    }

    const { annualizedReturn, grossReturn, standardDeviation } = historicalScenariosMetrics;

    return [
      {
        title: this.formatMessage(`${this.i18nBasePath}.Overview.Title`),
        fields: [
          {
            label: this.formatMessage('Charts.Growth.CumulativeReturn'),
            cellType: PercentageCell,
            value: grossReturn,
          },
          {
            label: this.formatMessage('Charts.Growth.AnnualizedReturn'),
            cellType: PercentageCell,
            value: annualizedReturn,
          },
          {
            label: this.formatMessage('RiskMetrics.StandardDeviation'),
            cellType: StatCell,
            value: standardDeviation,
          },
        ],
      },
    ];
  };
}
